import { Checkbox, SelectProps, Flex } from 'antd'
import { useState, useMemo, useCallback, useEffect } from 'react'
import Select, { SelectCompProps } from '../select/Select'
import styles from './SelectMultiple.module.less'
import { DataTestId } from 'src/constants/ui.constants'

export type SelectMultipleProps = Omit<SelectCompProps, 'value' | 'onChange'> & {
    dropdownStyle?: React.CSSProperties
    onChange?: (value: string[]) => void
    options: SelectProps['options']
    value?: string[]
    maxTextDisplayLength?: number
    onDropdownVisibleChange?: (visible: boolean) => void
}

const SelectMultiple: React.FC<SelectMultipleProps> = props => {
    const {
        options,
        onChange,
        height,
        width,
        style,
        value,
        maxTextDisplayLength,
        placeholder,
        onDropdownVisibleChange,
    } = props

    const [selectedOptions, setSelectedOptions] = useState<string[]>([])
    const [selectedValue, setSelectedValue] = useState<string | undefined>(undefined)

    const customStyle: React.CSSProperties = {
        height: height,
        width: width,
        ...style,
    }

    useEffect(() => {
        setSelectedOptions(value || [])
        setSelectedValue((value && value.length > 0 && value?.join(', ')) || undefined)
    }, [value])

    const handleCheckboxChange = useCallback(
        (option: string, checked: boolean) => {
            const updatedOptions = checked
                ? [...selectedOptions, option]
                : selectedOptions.filter(item => item !== option)

            const orderedUpdatedOptions = options
                ?.map(option => String(option.label))
                .filter(label => updatedOptions.includes(label))

            setSelectedOptions(orderedUpdatedOptions || [])
            const updatedValue = orderedUpdatedOptions?.join(', ') ?? ''
            setSelectedValue(updatedValue || undefined)

            if (onChange) {
                onChange(orderedUpdatedOptions || [])
            }
        },
        [selectedOptions, options, onChange]
    )

    const truncatedValue = useMemo(() => {
        return (value: string | undefined, truncationLength: number | undefined) => {
            if (!value || !truncationLength) {
                return value
            }

            if (value.length > truncationLength) {
                return value.slice(0, truncationLength) + ' ...'
            }
            return value
        }
    }, [])

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement
        if (e.key === 'Enter' && (!target.value || value?.includes(target.value))) {
            e.preventDefault()
            e.stopPropagation()
        }
    }

    return (
        <Select
            value={truncatedValue(selectedValue, maxTextDisplayLength)}
            optionRender={option => (
                <div onClick={e => e?.stopPropagation()} className={styles.option}>
                    <Flex align="center">
                        <Checkbox
                            checked={selectedOptions.includes(String(option.label))}
                            key={option.data.id}
                            onChange={e =>
                                handleCheckboxChange(String(option.label), e.target.checked)
                            }
                            data-testid={DataTestId.SelectMultipleCheckbox}
                        >
                            <span className={styles.optionLabel}>{option.label}</span>
                        </Checkbox>
                    </Flex>
                </div>
            )}
            dropdownRender={menu => <div className="select-multiple-override">{menu}</div>}
            options={options}
            placeholder={placeholder}
            style={customStyle}
            handleInputKeyDown={handleInputKeyDown}
            onDropdownVisibleChange={onDropdownVisibleChange}
        />
    )
}

export default SelectMultiple
